/**
 * ============================================================================
 *  File        : frontend/src/App.tsx
 *  Module      : root component
 *  Purpose     : React Router route map（spec §8.2 19 頁）、RBAC gating、locale
 *  Author      : Phoenix Lai
 *  Created     : 2026-05-30
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { useAuth } from './context/AuthContext'
import { usePermissions } from './hooks/usePermissions'
import { caseRoutes, type CaseRouteDef } from './data/caseRoutes'
import { normalizeLocale, translate } from './lib/i18n'
import { PERMISSIONS } from './lib/permissions'
import { DashboardPage } from './pages/DashboardPage'
import { LoginPage } from './pages/LoginPage'
import { CaseLayout } from './pages/CasePages'
import { UnderConstruction } from './pages/UnderConstruction'
import { CasesListPage } from './pages/CasesListPage'
import { CaseCreatePage } from './pages/CaseCreatePage'
import { CaseOverviewPage } from './pages/CaseOverviewPage'
import { CaseItemsPage } from './pages/CaseItemsPage'
import { CaseShipmentPage } from './pages/CaseShipmentPage'
import { CaseReceivingPage } from './pages/CaseReceivingPage'
import { CaseWeightAuditPage } from './pages/CaseWeightAuditPage'
import { CaseSettlementP1Page } from './pages/CaseSettlementP1Page'
import { CaseAuctionPage } from './pages/CaseAuctionPage'
import { CaseAdjustmentsPage } from './pages/CaseAdjustmentsPage'
import { CaseSharesPage } from './pages/CaseSharesPage'
import { CaseSettlementP2Page } from './pages/CaseSettlementP2Page'
import { CaseFinalWeightPage } from './pages/CaseFinalWeightPage'
import { CaseSettlementP3Page } from './pages/CaseSettlementP3Page'
import { CaseFinalReportPage } from './pages/CaseFinalReportPage'
import { AdminRolesPage, AdminUsersPage, AuditPage } from './pages/Workbenches'
import { ReportsPage } from './pages/ReportsPage'
import {
  MasterCategoriesPage,
  MasterMaterialsPage,
  MasterScalesPage,
  MasterVenuesPage,
  MasterWarehousesPage,
} from './pages/MasterPages'
import { SecurityPage } from './pages/SecurityPage'
import type { Locale } from './types'

const LOCALE_KEY = 'ef-platform.locale'

/**
 * 案件子頁元件對照表（依 caseRoutes.path 對應，spec §8.2 13 子頁）。
 * 13 個 caseRoutes 全部對到實作元件，無任何路由 fall through 到施工中 placeholder：
 *   settlements/p3 對到 CaseSettlementP3Page（設計上的 blocked skeleton：disabled
 *   試算 + 後端守門驗證，非 UnderConstruction）。
 * 缺漏即為開發期錯誤（見下方 dev-time 完整性檢查）。
 */
const CASE_SCREEN_COMPONENTS: Record<string, (props: { t: (key: string) => string }) => ReactElement> = {
  '': CaseOverviewPage,
  items: CaseItemsPage,
  shipment: CaseShipmentPage,
  receiving: CaseReceivingPage,
  'weight-audit': CaseWeightAuditPage,
  'settlements/p1': CaseSettlementP1Page,
  auction: CaseAuctionPage,
  adjustments: CaseAdjustmentsPage,
  shares: CaseSharesPage,
  'settlements/p2': CaseSettlementP2Page,
  'final-weight': CaseFinalWeightPage,
  'settlements/p3': CaseSettlementP3Page,
  'final-report': CaseFinalReportPage,
}

// dev-time 完整性檢查：每個 caseRoutes.path 都必須有對應元件，禁止 fall through。
if (import.meta.env.DEV) {
  const missing = caseRoutes.filter((def) => !CASE_SCREEN_COMPONENTS[def.path])
  if (missing.length > 0) {
    throw new Error(
      `caseRoutes missing component: ${missing.map((d) => d.path || '(overview)').join(', ')}`,
    )
  }
}

export function App(): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() =>
    normalizeLocale(localStorage.getItem(LOCALE_KEY) ?? navigator.language),
  )
  const auth = useAuth()

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(LOCALE_KEY, next)
    setLocaleState(next)
  }, [])

  const t = useCallback((key: string) => translate(locale, key), [locale])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const shell = useMemo(
    () => <AppShell locale={locale} setLocale={setLocale} t={t} />,
    [locale, setLocale, t],
  )

  if (!auth.isReady) {
    return <div className="boot-screen">{t('common.loading')}</div>
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage t={t} />} />
      <Route element={<Protected>{shell}</Protected>}>
        <Route index element={<DashboardPage t={t} />} />

        {/* 系統管理（spec §8.2 admin）*/}
        <Route path="admin" element={<Navigate replace to="/admin/users" />} />
        <Route
          path="admin/users"
          element={
            <RequirePermission permission={PERMISSIONS.adminUserManage} t={t}>
              <AdminUsersPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="admin/roles"
          element={
            <RequirePermission permission={PERMISSIONS.adminRoleManage} t={t}>
              <AdminRolesPage t={t} />
            </RequirePermission>
          }
        />
        {/* 主檔（spec §B）：品目 / 素材 / 場地 / 台秤 / 倉庫 5 個獨立全 CRUD 頁。
            全部受 system.configs.manage（masterPriceManage）gating。 */}
        <Route
          path="admin/master/categories"
          element={
            <RequirePermission permission={PERMISSIONS.masterPriceManage} t={t}>
              <MasterCategoriesPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="admin/master/materials"
          element={
            <RequirePermission permission={PERMISSIONS.masterPriceManage} t={t}>
              <MasterMaterialsPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="admin/master/venues"
          element={
            <RequirePermission permission={PERMISSIONS.masterPriceManage} t={t}>
              <MasterVenuesPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="admin/master/scales"
          element={
            <RequirePermission permission={PERMISSIONS.masterPriceManage} t={t}>
              <MasterScalesPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="admin/master/warehouses"
          element={
            <RequirePermission permission={PERMISSIONS.masterPriceManage} t={t}>
              <MasterWarehousesPage t={t} />
            </RequirePermission>
          }
        />
        {/* 既有單一主檔路徑 → 重導到第一個主檔頁（保留外部書籤可達）。 */}
        <Route
          path="admin/master-data"
          element={<Navigate replace to="/admin/master/categories" />}
        />
        <Route
          path="admin/master"
          element={<Navigate replace to="/admin/master/categories" />}
        />
        <Route path="master-data" element={<Navigate replace to="/admin/master/categories" />} />

        {/* 二要素認証（2FA / TOTP）設定 — 任何登入使用者皆可管理自己的 2FA（spec §A1）。 */}
        <Route path="admin/security" element={<SecurityPage t={t} />} />

        {/* 報表 / 稽核（spec §8.2）*/}
        <Route
          path="reports"
          element={
            <RequirePermission permission={PERMISSIONS.reportRead} t={t}>
              <ReportsPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="audit"
          element={
            <RequirePermission permission={PERMISSIONS.auditRead} t={t}>
              <AuditPage t={t} />
            </RequirePermission>
          }
        />

        {/* 案件列表 / 新增（spec §8.2 /cases, /cases/new）*/}
        <Route
          path="cases"
          element={
            <RequirePermission permission={PERMISSIONS.caseRead} t={t}>
              <CasesListPage t={t} />
            </RequirePermission>
          }
        />
        <Route
          path="cases/new"
          element={
            <RequirePermission permission={PERMISSIONS.caseCreate} t={t}>
              <CaseCreatePage t={t} />
            </RequirePermission>
          }
        />

        {/* 案件工作區 13 子頁（spec §8.2 /cases/:id/...）*/}
        <Route
          path="cases/:id"
          element={
            <RequirePermission permission={PERMISSIONS.caseRead} t={t}>
              <CaseLayout t={t} />
            </RequirePermission>
          }
        >
          {caseRoutes.map((def) => (
            // 13 子頁全部對到實作元件（見 CASE_SCREEN_COMPONENTS + dev 完整性檢查），
            // 無任何路由 fall through 到施工中 placeholder。
            <Route
              key={def.path || 'overview'}
              index={def.path === ''}
              path={def.path === '' ? undefined : def.path}
              element={
                <RequirePermission permission={def.permissionKey} t={t}>
                  <CaseScreenRoute def={def} t={t} />
                </RequirePermission>
              }
            />
          ))}
        </Route>
      </Route>
      <Route path="*" element={<Navigate replace to="/" />} />
    </Routes>
  )
}

function Protected({ children }: { children: ReactNode }): ReactElement {
  const auth = useAuth()
  const location = useLocation()
  if (!auth.isAuthenticated) {
    return <Navigate replace state={{ from: location }} to="/login" />
  }
  return <>{children}</>
}

/**
 * 解析案件子頁元件並渲染。CASE_SCREEN_COMPONENTS 已涵蓋全部 13 個 caseRoutes.path，
 * 此處不會回退到施工中 placeholder；若對照表缺漏（開發期錯誤）即拋出，由 dev
 * 完整性檢查與 baseline test 雙重把關。
 */
function CaseScreenRoute({
  def,
  t,
}: {
  def: CaseRouteDef
  t: (key: string) => string
}): ReactElement {
  const Screen = CASE_SCREEN_COMPONENTS[def.path]
  if (!Screen) {
    throw new Error(`caseRoutes missing component: ${def.path || '(overview)'}`)
  }
  return <Screen t={t} />
}

/** RBAC 路由守衛：無權限時導回 Dashboard（避免空白頁），nav 同步隱藏入口。 */
function RequirePermission({
  permission,
  t,
  children,
}: {
  permission?: string | null
  t: (key: string) => string
  children: ReactNode
}): ReactElement {
  const { can } = usePermissions()
  if (!can(permission)) {
    return (
      <UnderConstruction
        title={t('rbac.forbiddenTitle')}
        route={window.location.pathname}
        specSection="spec §9.2 權限不足"
        underConstructionLabel={t('rbac.forbidden')}
        description={t('rbac.forbiddenDesc')}
      />
    )
  }
  return <>{children}</>
}
